import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  DataGrid,
  GridToolbar,
  GridActionsCellItem,
  GridRowModes,
  GridRowModesModel,
  GridRowEditStopReasons
} from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  ContentCopy as ContentCopyIcon,
  DescriptionOutlined
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import CrearPlantillaDialog from './crearplantilladialog';
import DocumentPreview from './documentpreview';
import AlertMessageDialog from '../../../components/Dialogs/alertMessageDialog';

const PlantillasIndex = () => {
  const navigate = useNavigate();
  const [plantillas, setPlantillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCrearDialog, setOpenCrearDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [selectedPlantilla, setSelectedPlantilla] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [messageAlert, setMessageAlert] = useState('');
  const [openAlert, setOpenAlert] = useState(false);
  const [alertType, setAlertType] = useState('success');

  // DataGrid columns configuration
  const columns = [
    {
      field: 'nombre',
      headerName: 'Nombre',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight="medium" mt={1.5}>
            {params.value}
          </Typography>
        </Box>
      )
    },
    {
      field: 'tipo',
      headerName: 'Tipo',
      width: 150,
      renderCell: (params) => (
        <Chip
          label={params.value === 'contrato_prestamo' ? 'Contrato Préstamo' : params.value}
          size="small"
          color="primary"
          variant="outlined"
        />
      )
    },
    {
      field: 'producto_financiero_nombre',
      headerName: 'Producto Financiero',
      width: 200,
      renderCell: (params) => (
        <Typography
          variant="body2"
          mt={1.5}
          color={params.value ? 'text.primary' : 'text.secondary'}
          sx={{ fontStyle: params.value ? 'normal' : 'italic' }}
          noWrap
        >
          {params.value || 'Todos los productos'}
        </Typography>
      )
    },
    {
      field: 'activo',
      headerName: 'Estado',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Activo' : 'Inactivo'}
          size="small"
          color={params.value ? 'success' : 'default'}
          variant={params.value ? 'filled' : 'outlined'}
        />
      )
    },
    {
      field: 'created_at',
      headerName: 'Fecha Creación',
      width: 150,
      renderCell: (params) => {
        const date = new Date(params.value);
        return date.toLocaleDateString('es-ES');
      }
    },
    {
      field: 'created_by_name',
      headerName: 'Creado por',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary" justifyContent="center" textAlign="center" alignItems="center" mt={1.5}>
          {params.value || 'Sistema'}
        </Typography>
      )
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Acciones',
      width: 120,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<VisibilityIcon />}
          label="Vista Previa"
          onClick={() => handlePreview(params.row)}
        />,
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Editar"
          onClick={() => handleEdit(params.row)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Eliminar"
          onClick={() => handleDelete(params.row)}
        />
      ]
    }
  ];

  // Fetch plantillas from API
  const fetchPlantillas = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/plantillas/${localStorage.getItem('idcompany')}`, {
        headers: {
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPlantillas(data);
      } else {
        console.error('Error fetching plantillas');
        setMessageAlert('Error al cargar las plantillas');
        setAlertType('error');
        setOpenAlert(true);
      }
    } catch (error) {
      console.error('Error:', error);
      setMessageAlert('Error al cargar las plantillas');
      setAlertType('error');
      setOpenAlert(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlantillas();
  }, []);

  // Handle create new template
  const handleCreate = () => {
    setSelectedPlantilla(null);
    setOpenCrearDialog(true);
  };

  // Handle edit template
  const handleEdit = (plantilla) => {
    setSelectedPlantilla(plantilla);
    setOpenEditDialog(true);
  };

  // Handle preview template
  const handlePreview = (plantilla) => {
    setSelectedPlantilla(plantilla);
    setOpenPreviewDialog(true);
  };

  // Handle delete template
  const handleDelete = async (plantilla) => {
    if (window.confirm(`¿Estás seguro de que deseas eliminar la plantilla "${plantilla.nombre}"?`)) {
      try {
        const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/plantillas/${plantilla.id}`, {
          method: 'DELETE',
          headers: {
            'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
          }
        });

        if (response.ok) {
          setMessageAlert('Plantilla eliminada exitosamente');
          setAlertType('success');
          setOpenAlert(true);
          fetchPlantillas();
        } else {
          const error = await response.json();
          setMessageAlert(error.error || 'Error al eliminar la plantilla');
          setAlertType('error');
          setOpenAlert(true);
        }
      } catch (error) {
        console.error('Error:', error);
        setMessageAlert('Error al eliminar la plantilla');
        setAlertType('error');
        setOpenAlert(true);
      }
    }
  };

  // Handle dialog close
  const handleDialogClose = () => {
    setOpenCrearDialog(false);
    setOpenEditDialog(false);
    setOpenPreviewDialog(false);
    setSelectedPlantilla(null);
  };

  // Handle template saved
  const handleTemplateSaved = () => {
    handleDialogClose();
    fetchPlantillas();
    setMessageAlert('Plantilla guardada exitosamente');
    setAlertType('success');
    setOpenAlert(true);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box 
        sx={{ 
          background: 'linear-gradient(135deg, #18586b 0%, #55b4ac 100%)',
          borderRadius: 3,
          p: 4,
          mb: 4,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                color: 'white', 
                fontWeight: 700,
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}
            >
              <DescriptionOutlined sx={{ fontSize: 40 }} />
              Plantillas de Documentos
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
              Crea y gestiona plantillas personalizadas para contratos de préstamos con variables dinámicas
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            size="large"
            color='primarybutton'
            sx={{ 
              backgroundColor: 'white',
              color: '#18586b',
              fontWeight: 'bold',
              px: 3,
              py: 1.5,
              '&:hover': {
                backgroundColor: '#f0f0f0',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(0,0,0,0.2)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Crear Plantilla
          </Button>
        </Box>
      </Box>


      {/* DataGrid */}
      <Card sx={{ height: 'calc(100vh - 400px)', minHeight: '400px' }}>
        <CardContent sx={{ p: 0, height: '100%' }}>
          <DataGrid
            rows={plantillas}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            slots={{ toolbar: GridToolbar }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 }
              }
            }}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 25 }
              }
            }}
            pageSizeOptions={[25, 50, 100]}
            sx={{
              height: '100%',
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #f0f0f0'
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f8f9fa',
                borderBottom: '2px solid #e0e0e0',
                fontWeight: 'bold'
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 'bold'
              }
            }}
          />
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <CrearPlantillaDialog
        open={openCrearDialog || openEditDialog}
        onClose={handleDialogClose}
        onSave={handleTemplateSaved}
        plantilla={selectedPlantilla}
        isEdit={openEditDialog}
      />

      {/* Preview Dialog */}
      <DocumentPreview
        open={openPreviewDialog}
        onClose={handleDialogClose}
        plantilla={selectedPlantilla}
      />

      {/* Alert Message Dialog */}
      <AlertMessageDialog
        open={openAlert}
        handleClose={() => setOpenAlert(false)}
        message={messageAlert}
        severity={alertType}
        title=""
      />
    </Box>
  );
};

export default PlantillasIndex;
